import type { PreviewLine } from './preview';

type Fields = Record<string, unknown>;
export interface CodexEntry {
  id: string;
  kind: 'message' | 'tool' | 'reasoning' | 'event';
  title: string;
  text: string;
  line: number;
  sourceLines: number[];
  timestamp: string;
  role?: 'user' | 'assistant' | 'system';
  callId?: string;
  input?: string;
  output?: string;
  hasCall?: boolean;
  status?: 'unresolved' | 'returned' | 'succeeded' | 'failed';
  exitCode?: number;
  durationMs?: number;
  phase?: string;
}
export interface CodexTranscript {
  entries: CodexEntry[];
  unreadableLines: number;
  metadataLines: number;
  model: string;
}

function fields(value: unknown): Fields {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Fields)
    : {};
}
function string(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// Content blocks stay text. Media/opaque reasoning are represented by markers;
// the viewer never follows URLs or evaluates tool input found in a transcript.
function contentText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    return value
      .map((part) => {
        if (typeof part === 'string') return part;
        const block = fields(part);
        if (typeof block.text === 'string') return block.text;
        if (block.type === 'image' || block.type === 'input_image')
          return '[图片内容，请查看原文记录]';
        if (block.type === 'audio' || block.type === 'input_audio')
          return '[音频内容，请查看原文记录]';
        if (block.type === 'encrypted_content')
          return '[加密内容，无法还原正文]';
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  const object = fields(value);
  if (Array.isArray(object.content)) return contentText(object.content);
  if (typeof object.text === 'string') return object.text;
  return value === undefined ? '' : JSON.stringify(value, null, 2);
}
function toolInput(value: unknown): string {
  return typeof value === 'string'
    ? value
    : value === undefined
      ? ''
      : JSON.stringify(value, null, 2);
}
function parseRecord(line: PreviewLine): Fields | undefined {
  if (!line.complete || line.text.length > 1024 * 1024) return undefined;
  try {
    const value: unknown = JSON.parse(line.text);
    const stack = [{ value, depth: 0 }];
    let nodes = 0;
    while (stack.length) {
      const current = stack.pop();
      if (!current || ++nodes > 20000 || current.depth > 64) return undefined;
      if (current.value !== null && typeof current.value === 'object') {
        if (Array.isArray(current.value) && current.value.length > 20000)
          return undefined;
        const children = Object.values(current.value);
        if (children.length + nodes + stack.length > 20000) return undefined;
        for (const child of children)
          stack.push({ value: child, depth: current.depth + 1 });
      }
    }
    const record = fields(value);
    return typeof record.type === 'string' ? record : undefined;
  } catch {
    return undefined;
  }
}

const metadataTypes = new Set([
  'session_meta',
  'turn_context',
  'token_usage_record',
  'world_state',
  'inter_agent_communication_metadata',
]);
const bookkeepingEvents = new Set([
  'token_count',
  'thread_settings_applied',
  'session_configured',
  'agent_message_delta',
  'agent_reasoning_delta',
  'exec_command_output_delta',
  'exec_command_begin',
  'mcp_tool_call_begin',
  'patch_apply_begin',
]);

// Normalize the loaded prefix, not the whole session. Rebuilding on a new chunk
// lets an output received later attach to its original call without a second card.
export function buildCodexTranscript(lines: PreviewLine[]): CodexTranscript {
  const result: CodexTranscript = {
    entries: [],
    unreadableLines: 0,
    metadataLines: 0,
    model: '',
  };
  const tools = new Map<string, CodexEntry>();
  const messages = new Map<string, CodexEntry>();
  const occurrences = new Map<string, number>();
  let currentTurn = '';
  for (const line of lines) {
    const record = parseRecord(line);
    if (!record) {
      result.unreadableLines++;
      continue;
    }
    const payload = fields(record.payload);
    const item = fields(payload.item);
    const timestamp = string(record.timestamp);
    const turn =
      string(payload.turn_id) ||
      string(
        fields(payload.internal_chat_message_metadata_passthrough).turn_id,
      ) ||
      currentTurn;
    if (payload.turn_id) currentTurn = string(payload.turn_id);
    const entry = (
      kind: CodexEntry['kind'],
      title: string,
      text = '',
    ): CodexEntry => ({
      id: `${kind}-${line.number}`,
      kind,
      title,
      text,
      line: line.number,
      sourceLines: [line.number],
      timestamp,
    });
    const message = (
      role: 'user' | 'assistant' | 'system',
      text: string,
      source: string,
      phase = '',
    ) => {
      if (!text) return;
      const key = JSON.stringify([turn, role, text]);
      const occurrenceKey = `${source}:${key}`;
      const index = occurrences.get(occurrenceKey) ?? 0;
      occurrences.set(occurrenceKey, index + 1);
      const identity = `${key}:${index}`;
      const existing = messages.get(identity);
      if (existing) {
        existing.sourceLines.push(line.number);
        if (phase) existing.phase = phase;
        return;
      }
      const value = {
        ...entry(
          'message',
          role === 'user'
            ? '用户'
            : role === 'assistant'
              ? 'Codex'
              : '上下文指令',
          text,
        ),
        role,
        phase,
      };
      messages.set(identity, value);
      result.entries.push(value);
    };
    const tool = (callId: string): CodexEntry => {
      let value = callId ? tools.get(callId) : undefined;
      if (!value) {
        value = {
          ...entry('tool', '未关联的工具结果'),
          callId,
          hasCall: false,
          status: 'unresolved',
        };
        if (callId) tools.set(callId, value);
        result.entries.push(value);
      } else if (!value.sourceLines.includes(line.number)) {
        value.sourceLines.push(line.number);
      }
      return value;
    };
    if (record.type === 'turn_context')
      result.model = string(payload.model) || result.model;
    if (metadataTypes.has(string(record.type))) {
      result.metadataLines++;
      continue;
    }
    if (record.type === 'response_item') {
      const type = string(payload.type);
      if (type === 'message') {
        const role = payload.role;
        if (
          role !== 'user' &&
          role !== 'assistant' &&
          role !== 'developer' &&
          role !== 'system'
        ) {
          result.unreadableLines++;
          continue;
        }
        const kinds = fields(
          payload.internal_chat_message_metadata_passthrough,
        ).content_item_kinds;
        const content = Array.isArray(payload.content) ? payload.content : [];
        const hasHumanKinds =
          Array.isArray(kinds) && kinds.includes('user.text');
        const hasInjectedKinds =
          Array.isArray(kinds) &&
          kinds.some((kind) => typeof kind === 'string' && kind !== 'unknown');
        const humanContent =
          role === 'user' && hasHumanKinds
            ? content.filter((_, index) => kinds[index] === 'user.text')
            : content;
        // Harness-injected user context is retained in raw view, not shown as a prompt.
        if (role === 'user' && hasInjectedKinds && !hasHumanKinds) {
          result.metadataLines++;
          continue;
        }
        message(
          role === 'user' || role === 'assistant' ? role : 'system',
          contentText(humanContent),
          'response',
          string(payload.phase),
        );
      } else if (type === 'function_call' || type === 'custom_tool_call') {
        const value = tool(string(payload.call_id));
        if (!value.hasCall) {
          value.line = line.number;
          value.timestamp = timestamp;
        }
        value.title = string(payload.name) || '工具调用';
        value.hasCall = true;
        value.input = toolInput(
          type === 'function_call' ? payload.arguments : payload.input,
        );
      } else if (
        type === 'function_call_output' ||
        type === 'custom_tool_call_output'
      ) {
        const value = tool(string(payload.call_id));
        value.output = contentText(payload.output);
        if (
          payload.is_error === true ||
          fields(payload.output).isError === true
        )
          value.status = 'failed';
        else if (value.status === 'unresolved') value.status = 'returned';
      } else if (type === 'reasoning') {
        const text =
          contentText(payload.summary) || contentText(payload.content);
        result.entries.push(
          entry(
            'reasoning',
            '推理摘要',
            text ||
              (payload.encrypted_content
                ? '此记录仅含加密内容，无法还原正文。'
                : '此记录未保存可读摘要。'),
          ),
        );
      } else if (type === 'agent_message') {
        result.entries.push(
          entry(
            'event',
            `Agent 消息 · ${string(payload.author)} → ${string(payload.recipient)}`,
            contentText(payload.content) || '未保存可读消息正文。',
          ),
        );
      } else if (type === 'web_search_call') {
        const value = tool(string(payload.id));
        value.title = 'web_search';
        value.hasCall = true;
        value.input = toolInput(payload.action);
        if (payload.status === 'completed') value.status = 'returned';
      } else result.unreadableLines++;
      continue;
    }
    if (record.type === 'event_msg') {
      const type = string(payload.type);
      if (type === 'user_message' || type === 'agent_message') {
        message(
          type === 'user_message' ? 'user' : 'assistant',
          string(payload.message),
          'legacy',
          string(payload.phase),
        );
      } else if (type === 'item_completed') {
        const completedType = string(item.type);
        if (
          completedType === 'UserMessage' ||
          completedType === 'AgentMessage'
        ) {
          message(
            completedType === 'UserMessage' ? 'user' : 'assistant',
            contentText(item.content),
            'completed',
            string(item.phase),
          );
        } else if (
          completedType === 'CommandExecution' ||
          completedType === 'McpToolCall' ||
          completedType === 'FileChange'
        ) {
          const value = tool(string(item.id));
          if (!value.hasCall) {
            value.title =
              completedType === 'CommandExecution'
                ? 'exec_command'
                : completedType === 'FileChange'
                  ? 'apply_patch'
                  : [string(item.server), string(item.tool)]
                      .filter(Boolean)
                      .join('.');
            value.input =
              completedType === 'CommandExecution'
                ? toolInput(item.command)
                : completedType === 'FileChange'
                  ? toolInput(item.changes)
                  : toolInput(item.arguments);
          }
          value.hasCall = true;
          if (value.output === undefined)
            value.output =
              completedType === 'McpToolCall'
                ? contentText(item.error ?? item.result)
                : string(item.aggregated_output) ||
                  [string(item.stdout), string(item.stderr)]
                    .filter(Boolean)
                    .join('\n');
          if (typeof item.exit_code === 'number')
            value.exitCode = item.exit_code;
          const duration = fields(item.duration);
          if (typeof duration.secs === 'number')
            value.durationMs =
              duration.secs * 1000 +
              (typeof duration.nanos === 'number' ? duration.nanos / 1e6 : 0);
          const failed =
            value.status === 'failed' ||
            item.status === 'failed' ||
            fields(item.result).isError === true ||
            (value.exitCode !== undefined && value.exitCode !== 0);
          value.status = failed
            ? 'failed'
            : item.status === 'completed'
              ? 'succeeded'
              : 'returned';
        } else if (completedType === 'Reasoning') {
          const summary = contentText(item.summary_text);
          if (summary)
            result.entries.push(entry('reasoning', '推理摘要', summary));
          else result.metadataLines++;
        } else if (completedType === 'Extension') {
          const value = tool(string(item.id));
          value.title = string(item.kind) || '扩展工具';
          value.hasCall = true;
          value.input = toolInput(item.action ?? item.query);
          if (item.results !== undefined)
            value.output = toolInput(item.results);
          value.status = 'returned';
        } else if (completedType === 'SubAgentActivity') {
          if (!tools.has(string(item.id)))
            result.entries.push(
              entry(
                'event',
                'Agent 活动',
                [string(item.kind), string(item.agent_path)]
                  .filter(Boolean)
                  .join(' · '),
              ),
            );
          else result.metadataLines++;
        } else if (completedType === 'ContextCompaction') {
          result.entries.push(entry('event', '上下文压缩'));
        } else result.unreadableLines++;
      } else if (type === 'task_started' || type === 'task_complete') {
        result.entries.push(
          entry('event', type === 'task_started' ? '开始处理' : '本轮完成'),
        );
      } else if (bookkeepingEvents.has(type)) result.metadataLines++;
      else result.unreadableLines++;
      continue;
    }
    if (record.type === 'compacted')
      result.entries.push(
        entry('event', '上下文压缩摘要', string(payload.message)),
      );
    else result.unreadableLines++;
  }
  result.entries.sort((a, b) => a.line - b.line);
  return result;
}
